import { useTheme } from "@mui/material";
import { useAppSelector } from "../../../index";
import { ApplicationState } from "@store/index";
import React, { useEffect, useRef, useState } from "react";
import NoDataComponent from "../../Components/NoDataComponent";
import { Category, DataPoint } from "../../../fileParsing/types";
import { GroupColor, groupColor, GroupMapping } from "./group";
import { useDatapointsFromExportId } from "../../hooks/useData";
import * as d3 from "d3";
import { Theme } from "@mui/system";
import { ResizeComponent, SizeProps } from "../../hooks/ResizeComponent";
import Tooltip from "containers/Components/Tooltip";

// Data Structure for Sunburst
class SunBurstTree extends Map<string, SunBurstTree> {
  public name: string;
  public size: number;
  public depth: number;
  public parent?: string;
  public groupCategory?: string;
  public category: Category;

  constructor(params?: {
    name?: string;
    size?: number;
    depth?: number;
    parent?: string;
    groupCategory?: string;
    category?: Category;
  }) {
    super();
    const { name, size, depth, parent, groupCategory, category } = params || {};
    this.name = name || "";
    this.size = size || 0;
    this.depth = depth || 0;
    this.parent = parent;
    this.groupCategory = groupCategory;
    this.category = category ?? Category.Other;
  }
}

// Sunburst-Plot Settings
export type VisualizationSettings = {
  width: number;
  height: number;
  padding: { left: number; top: number; right: number; bottom: number };
  innerRadiusFactor: number;
  ringSpacing: number;
  paddingAngle: number;
  tooltipOpacity: number;
  tooltipMaxWidth: number;
  hoverOpacity: number;
  iconSize: number;
};

// Default Sunburst-Plot Settings
export const VisualizationSettingsDefaults: VisualizationSettings = {
  width: 1024,
  height: 1024,
  padding: { left: 20, top: 20, right: 20, bottom: 20 },
  innerRadiusFactor: 0.1,
  ringSpacing: 3,
  paddingAngle: 0.015,
  tooltipOpacity: 0.9,
  tooltipMaxWidth: 320,
  hoverOpacity: 0.25,
  iconSize: 64,
};

// Component-specific props
type ComponentProps = {
  exportId?: number;
  settings?: Partial<VisualizationSettings>;
};

// Data type adapter - Converts DataPoints[] into a Sunburst Tree
const transformData = (data: DataPoint[]): SunBurstTree => {
  const tree = new SunBurstTree();

  data.forEach((d) => {
    let group = tree.get("" + GroupMapping[d.category]);
    if (group === undefined) {
      group = new SunBurstTree({
        name: "" + GroupMapping[d.category],
        parent: "",
        depth: 1,
        groupCategory: GroupMapping[d.category],
        category: d.category,
      });
      tree.set(GroupMapping[d.category], group);
    }

    let category = group.get(d.category);
    if (category === undefined) {
      category = new SunBurstTree({
        name: "" + d.category,
        parent: "" + GroupMapping[d.category],
        depth: 2,
        groupCategory: d.category,
        category: d.category,
      });
      group.set(d.category, category);
    }

    let subcategory = category.get(d.subcategory);
    if (subcategory === undefined) {
      subcategory = new SunBurstTree({
        name: d.subcategory,
        parent: "" + d.category,
        depth: 3,
        groupCategory: d.category,
        category: d.category,
      });
      category.set(d.subcategory, subcategory);
    }

    subcategory.size += 1;
    category.size += 0;
    group.size += 0;
    tree.size += 0;
  });

  return tree;
};

const getColor = (node: d3.HierarchyRectangularNode<SunBurstTree>, theme: Theme): string => {
  if (node.data === undefined) return theme.palette.background.paper;

  const cat = Object.values(node.data)[1].category;

  if (cat === undefined) return theme.palette.background.paper;

  if (Object.keys(GroupColor).includes(cat)) return groupColor(cat);
  else if (Object.keys(theme.palette.categories).includes(cat)) return theme.palette.categories[cat].main;
  else return theme.palette.background.paper;
};

const createTooltipTexts = (d: d3.HierarchyRectangularNode<SunBurstTree>) => {
  const ancestorsTooltip = d.ancestors().reverse().slice(1);
  const numDatapoints = d3.format(",d")(d.value || 0);
  const numa = d3.format(".1%")((d.value || 0) / (d.parent?.value || 1));
  const numb = d.parent?.data.entries().next().value?.at(1) || 0;
  const amount = d.parent ? `${numa} of ${numb || "all data points"}` : "";

  const breadCrumbs = ancestorsTooltip
    .map((a: d3.HierarchyRectangularNode<SunBurstTree>) => `${a.data.entries().next().value?.at(1)}`)
    .join(" → ");

  return {
    title: breadCrumbs,
    subtitle: "",
    body: `${numDatapoints} data points\n(${amount})`,
  };
};

const getCategory = (d: d3.HierarchyRectangularNode<SunBurstTree>): Category => {
  const category = [...d.data.values()][1].category;
  return category ?? Category.Other;
};

function SunBurst(props: ComponentProps & SizeProps) {
  // Used constants
  const settings = {
    ...VisualizationSettingsDefaults,
    ...props.settings,
    width: props.size.width,
    height: props.size.height,
  };

  const theme = useTheme();
  const items = useAppSelector((state: ApplicationState) => state.data.exports);

  const fullData = useDatapointsFromExportId(props.exportId);
  const innerRadius = Math.min(settings.width, settings.height) * settings.innerRadiusFactor;
  const fullTree = transformData(fullData);

  // Local State
  const [currentTree, setCurrentTree] = useState<SunBurstTree>(fullTree);
  const [tooltipPos, setTooltipPos] = useState({
    x: 0,
    y: 0,
  });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipTexts, setTooltipTexts] = useState({
    title: "",
    subtitle: "",
    body: "",
  });
  const [tooltipCategory, setTooltipCategory] = useState<Category>(Category.Other);

  // Local Refs
  const d3Container = useRef<SVGSVGElement>(null);
  const centerLabel = useRef<SVGSVGElement>(null);

  // useEffects
  useEffect(() => {
    setCurrentTree(transformData(fullData));
  }, [fullData.length, settings.width, settings.height]);

  useEffect(() => {
    drawSunburst();
  }, [currentTree]);

  // Functions
  function drawSunburst() {
    if (!d3Container.current) return;

    const tree = d3
      .hierarchy(currentTree, (d) => {
        return Array.isArray(d) ? d[1] : null;
      })
      .sum((d) => (Array.isArray(d) ? d[1].size : 0))
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    const sunburst = d3.partition<SunBurstTree>().size([2 * Math.PI, tree.height + 1])(tree);

    const arc = d3
      .arc<d3.HierarchyRectangularNode<SunBurstTree>>()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .padAngle((d) => Math.min((d.x1 - d.x0) / 2, settings.paddingAngle))
      .padRadius(innerRadius * 1.5)
      .innerRadius((d) => d.y0 * innerRadius + settings.ringSpacing)
      .outerRadius((d) => Math.max(d.y0 * innerRadius, d.y1 * innerRadius - 1));

    const node = d3.select(d3Container.current);
    setTooltipVisible(false);

    const draw = node
      .selectAll<SVGGElement, unknown>("path")
      .data(sunburst.descendants().slice(1))
      .join("path")
      .attr("fill", (d) => getColor(d, theme))
      .attr("d", arc);

    // Event-Listener
    const onDrillDown = (_e: Event, p: d3.HierarchyRectangularNode<SunBurstTree>) => {
      if (!Array.isArray(p.data)) return;
      if ([...p.data[1].entries()].length === 0) return;
      setCurrentTree(p.data[1]);
    };

    const mouseover = function (_e: Event, d: d3.HierarchyRectangularNode<SunBurstTree>) {
      // Add Centerlabel
      if (centerLabel.current) {
        d3.select(centerLabel.current).style("visibility", "visible");
        d3.select(`#centerLabelPercentage_${props.exportId ?? 1}`).text(
          `${d3.format(".1%")((d.value || 0) / (tree.value || 1))}`,
        );
      }

      // Show Tooltip
      setTooltipTexts(createTooltipTexts(d));
      setTooltipCategory(getCategory(d));
      setTooltipVisible(true);

      // highlight path of ancestors
      const ancestors = d.ancestors().map((d) => d.data.entries().next().value?.at(1));

      draw
        .filter((d) => !ancestors.includes(d.data.entries().next().value?.at(1)) && d.depth !== 0)
        .style("opacity", settings.hoverOpacity);
    };

    const mouseleave = () => {
      //Remove Centerlabel
      if (centerLabel.current) {
        d3.select(centerLabel.current).style("visibility", "hidden");
      }

      // Remove Tooltip
      // tooltip.style("visibility", "hidden ");
      setTooltipVisible(false);

      // Make all Arcs visible again
      draw.filter((d) => d.depth !== 0).style("opacity", 1);
    };

    const mousemove = (e: MouseEvent) => {
      setTooltipPos({ x: e.pageX, y: e.pageY });
    };

    // Add Drilldown-Listener to all Arcs
    draw
      .filter((d) => d.children !== undefined && d.children.length > 0)
      .style("cursor", "pointer")
      .on("click", onDrillDown);

    // Remove Drilldown-Listener from leaf-arcs
    draw
      .filter((d) => d.children === undefined)
      .style("cursor", "default")
      .on("click", () => {
        /** */
      });

    // Add Tooltip and tracing listener
    draw.on("mouseover", mouseover).on("mousemove", mousemove).on("mouseleave", mouseleave);
  }

  // Event-Handler
  const onDrillUp = () => setCurrentTree(transformData(fullData));

  return (
    <>
      {items.length > 0 ? (
        <svg
          className="sunburst"
          width="100%"
          height={settings.height}
          viewBox={`0 0 ${settings.width} ${settings.height}`}
        >
          <rect
            onClick={onDrillUp}
            width={settings.width}
            height={settings.height}
            fill={theme.palette.background.paper}
          />

          <g ref={d3Container} transform={`translate(${[settings.width / 2, settings.height / 2]})`} />

          <g
            transform={`translate(${[settings.width / 2, settings.height / 2]})`}
            style={{ visibility: "hidden", mixBlendMode: "difference" }}
            ref={centerLabel}
          >
            <text
              id={`centerLabelPercentage_${props.exportId ?? 1}`}
              dy={-0.15 * innerRadius}
              textAnchor="middle"
              fill="white"
              fontSize={innerRadius * 0.5}
              fontVariant="small-caps"
              fontWeight="bold"
              strokeWidth={1}
              stroke="black"
              strokeOpacity={0.3}
              style={{ cursor: "default", pointerEvents: "none" }}
            ></text>
            <text
              dy={0.2 * innerRadius}
              textLength={innerRadius * 1.75}
              textAnchor="middle"
              fill="white"
              fontSize={innerRadius * 0.2}
              fontVariant="small-caps"
              fontWeight="bold"
              strokeWidth={0.5}
              stroke="black"
              strokeOpacity={0.3}
              style={{ cursor: "default", pointerEvents: "none" }}
            >
              of selected data
            </text>
          </g>
        </svg>
      ) : (
        <NoDataComponent />
      )}

      <Tooltip
        settings={{
          tooltipWidth: settings.tooltipMaxWidth,
          margin: 8,
        }}
        category={tooltipCategory}
        visible={tooltipVisible}
        position={tooltipPos}
        texts={tooltipTexts}
      ></Tooltip>
    </>
  );
}

export default ResizeComponent(SunBurst);
